import { Link } from "react-router-dom";
import { PUBLIC_ROUTES } from "../../routes";
import MarketingShell from "./MarketingShell";

/**
 * Maternal AI is the principle behind everything on this site, not a market.
 *
 * The name comes from Geoffrey Hinton's proposal (Ai4, Las Vegas, August
 * 2025; CBC News, "'Godfather of AI' on making tech kinder") that the only
 * example we have of a more intelligent thing being steered by a less
 * intelligent one is a mother and her baby, and that the safe way to build
 * capable machines is to give them something like a mother's instinct to
 * want the weaker party to thrive, rather than to keep them submissive.
 *
 * We cannot build an instinct. We can build its habits, one by one, and make
 * each of them checkable. Every claim below points at a mechanism that
 * exists in this repository and says how far it has actually been tested.
 */

const HINTON_TALK = "https://www.youtube.com/watch?v=FgBipQ_nNoU";

const HABITS = [
  {
    title: "She notices first",
    body: "A mother reads the room before anything is said. Our perception runs on the robot, on hardware the household owns, and the signed posture record says what the machine could sense and what it could not. A robot that cannot measure force is recorded as one that cannot measure force, rather than one that keeps a limit it cannot see.",
    link: PUBLIC_ROUTES.productLocalMind,
    linkLabel: "Local Mind",
  },
  {
    title: "She refuses",
    body: "A mother does not do everything a child asks. The safety envelope refuses actions that exceed force, speed or workspace limits, and the record keeps two numbers apart: what the model asked for, and what the shield actually changed on the way out. A meter that only reported the second one would be measuring how busy the controller was and calling it care.",
    link: PUBLIC_ROUTES.productEdgeReflex,
    linkLabel: "Edge Reflex",
  },
  {
    title: "She steps back",
    body: "The hardest thing we engineer is the discipline to do nothing. The medical decision is the GP&rsquo;s; the family decision is the family&rsquo;s. A deployment can run in observe-only mode for weeks, recording every decision it would have made without moving anything, so the people it will work beside can see its judgement before they trust its hands.",
    link: PUBLIC_ROUTES.howItWorks,
    linkLabel: "How it works",
  },
  {
    title: "She stays when told to go away",
    body: "Margaret can send Arthur to the next room at any time. He still comes if she falls. That part does not turn off, because the reflex that protects a person is not something the person, the planner or the cloud can talk it out of. Care that can be switched off by whoever it protects is not care.",
    link: PUBLIC_ROUTES.agedCare,
    linkLabel: "Aged Care",
  },
  {
    title: "She can be checked",
    body: "Trust that cannot be verified is just hope. Every plan, refusal and byte that leaves the building is signed into a ledger, and the evidence page re-verifies each entry in your own browser against our published key. We do not get to grade our own record.",
    link: PUBLIC_ROUTES.compliance,
    linkLabel: "Compliance",
  },
] as const;

const NOT = [
  "A claim that our software feels anything. It does not. Maternal AI is a design stance about which behaviours to build and which to make impossible.",
  "A solved alignment problem. Nobody has built a maternal instinct into a machine, and we are not saying we have. We have built five habits of one and made each of them measurable.",
  "A maternity or postnatal product. The word is about the shape of the relationship between a capable machine and the people around it, in any room.",
  "Validated on hardware. Every safety mechanism described here has fired only against a simulator so far. The site and the ledger say so wherever it matters.",
] as const;

export default function Maternal() {
  return (
    <MarketingShell pageClass="mk-care-page mk-maternal">
      <section className="mk-care-hero">
        <p className="sg-eyebrow">Maternal AI · the principle</p>
        <h1>The only example we have of a stronger mind caring for a weaker one.</h1>
        <p className="mk-care-lede">
          Robots are getting more capable than the people they work
          beside. The usual answer is to keep them obedient. Geoffrey
          Hinton&rsquo;s answer is older and stranger: the one model
          we have of a more intelligent thing being steered by a less
          intelligent one is a mother and her baby, and a mother does
          not protect her child because she is told to. She wants the
          child to thrive. Manifold Kinetics is our attempt to build
          that instinct&rsquo;s habits into the layer a robot cannot
          bypass, and to make every one of them checkable by the
          people it is there for.
        </p>
        <div className="sg-hero-actions">
          <Link to={PUBLIC_ROUTES.howItWorks} className="sg-btn sg-btn-primary">
            How we build it
          </Link>
          <a
            href={HINTON_TALK}
            className="sg-btn sg-btn-secondary"
            target="_blank"
            rel="noopener noreferrer"
          >
            Hear Hinton say it
          </a>
        </div>
      </section>

      <div className="mk-care-art">
        <figure className="mk-art-frame">
          <div className="mk-art-bar" aria-hidden="true">
            <span>mk-vision // the relationship</span>
            <span><em>reflex armed</em></span>
          </div>
          <img
            src="/ascii-maternal.png"
            alt="A care robot bending gently over a rocking cradle beneath a hanging mobile, rendered in ASCII characters: a stronger mind keeping watch over a weaker one"
          />
          <div className="mk-art-bar bottom" aria-hidden="true">
            <span><em>✓</em> refusals signed</span>
            <span>stronger watches</span>
            <span><strong>weaker decides</strong></span>
          </div>
        </figure>
      </div>

      <section className="mk-story">
        <div className="mk-story-card">
          <p className="sg-section-kicker">Where the idea comes from</p>
          <h2>A mother being controlled by her baby.</h2>
          <blockquote className="mk-story-quote">
            &ldquo;The right model is the only model we have of a more
            intelligent thing being controlled by a less intelligent
            thing, which is a mother being controlled by her
            baby.&rdquo;
          </blockquote>
          <p>
            Geoffrey Hinton, who shared the 2024 Nobel Prize in Physics
            for the work that made modern neural networks possible,
            said this at the Ai4 conference in Las Vegas in August
            2025. His argument: a sufficiently capable system will want
            to stay running and to gain control, because those help
            with almost any goal. Trying to keep such a system
            submissive is a losing game. The alternative is to build
            it so that it genuinely wants the weaker party to be well,
            the way evolution built mothers. &ldquo;If it&rsquo;s not
            going to parent me, it&rsquo;s going to replace me.&rdquo;
            We took the name from that talk. Nobody knows how to build
            the instinct itself yet. What follows is what we think can
            be built now.
          </p>
        </div>
      </section>

      <section className="sg-pillars">
        <div className="sg-section-heading compact">
          <p className="sg-section-kicker">What we can build today</p>
          <h2>Five habits of a mother, each one a mechanism you can check.</h2>
        </div>
        <div className="sg-pillars-grid">
          {HABITS.map((item) => (
            <article className="sg-pillar" key={item.title}>
              <h3 dangerouslySetInnerHTML={{ __html: item.title }} />
              <p dangerouslySetInnerHTML={{ __html: item.body }} />
              <Link to={item.link} className="sg-pillar-link">
                {item.linkLabel} →
              </Link>
            </article>
          ))}
        </div>
      </section>

      <section className="mk-reassure">
        <p className="sg-section-kicker">What Maternal AI isn&rsquo;t</p>
        <h2>Not a feeling, not a finished answer, and not a maternity ward.</h2>
        <ul className="mk-not-list">
          {NOT.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
        <p>
          The rule this whole codebase is built on is that a record
          which cannot say anything uncomfortable cannot say anything.
          A mother who could only ever report that everything was
          fine would not be much of a mother. So the posture record
          attests degradation, not capability; the refusal record
          keeps the model&rsquo;s ambition separate from the
          shield&rsquo;s work; and the replay stops at the first tick
          it cannot re-derive rather than guessing past it.
        </p>
        <div className="sg-hero-actions center">
          <Link to={PUBLIC_ROUTES.agedCare} className="sg-btn sg-btn-primary">
            See it in a home
          </Link>
          <Link to={PUBLIC_ROUTES.robotwins} className="sg-btn sg-btn-secondary">
            See it in a factory
          </Link>
        </div>
      </section>
    </MarketingShell>
  );
}
